import { shouldRefetchTradeQuery } from '@/client/components/trade/trade-success-queries';
import {
  matchPendingTrade,
  nextReconcileDelay,
  RECONCILE_TIMEOUT_MS,
  usePendingTradesStore,
} from '@/client/store/pending-trades-store';
import { api } from '@/client/utils/api';
import type { MarketOrderInfo } from '@/libs/sdk/api';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

type ScheduleEntry = { timer: ReturnType<typeof setTimeout>; attempt: number };

// After the 90s syncSlow cutoff, stop backing off and settle into this fixed,
// low-frequency cadence — polling never stops until a match is found, it just
// gets cheap. Without this a syncSlow row polls exactly once more (see the old
// behavior this replaces) and then sits duplicated against the real backend
// row for the rest of the SPA session once the indexer finally catches up.
const SYNC_SLOW_POLL_MS = 30_000;

// Polls the (txHash-less) backend trade feed for each optimistic row until it
// shows up (matchPendingTrade). Past 90s the row flips to syncSlow (the
// trade-gate releases, the row stays visible with a reassurance message) but
// polling continues at SYNC_SLOW_POLL_MS until a match removes it — otherwise
// the real row would arrive alongside a permanent phantom duplicate. One
// global instance (mounted via <PendingTradesReconciler /> in AppModels) so
// polling survives page navigation.
export const usePendingTradeReconciler = (): void => {
  const queryClient = useQueryClient();
  const trades = usePendingTradesStore(state => state.trades);
  const scheduledRef = useRef<Map<string, ScheduleEntry>>(new Map());

  useEffect(() => {
    const scheduled = scheduledRef.current;

    const tick = async (txHash: string) => {
      const trade = usePendingTradesStore.getState().trades[txHash];
      // Fully gone from the store (matched via another path, or
      // clearAllPendingTrades) — nothing left to reconcile.
      if (!trade) {
        scheduled.delete(txHash);
        return;
      }

      if (trade.status === 'pending' && Date.now() - trade.createdAt >= RECONCILE_TIMEOUT_MS) {
        usePendingTradesStore.getState().markSyncSlow(txHash);
      }

      let orders: MarketOrderInfo[] = [];
      try {
        const { data } = await api.market.getMarketTrades({
          baseTokenAddress: trade.baseTokenAddress,
          marketAddress: trade.marketAddress,
          page: 1,
          pageSize: 20,
        });
        orders = data?.orders ?? [];
      } catch {
        // Network/API failure counts as "no match yet" — keep polling rather
        // than surfacing an error for a tx that already confirmed on-chain.
        orders = [];
      }

      const consumedOrderUuids = usePendingTradesStore.getState().consumedOrderUuids;
      const hit = matchPendingTrade(trade, orders, consumedOrderUuids);
      if (hit) {
        usePendingTradesStore.getState().markOrderConsumed(hit);
        usePendingTradesStore.getState().removePendingTrade(txHash);
        scheduled.delete(txHash);
        queryClient.refetchQueries({
          predicate: query =>
            shouldRefetchTradeQuery(query.queryKey, {
              uid: trade.uid,
              baseTokenAddress: trade.baseTokenAddress,
              marketAddress: trade.marketAddress,
            }),
        });
        return;
      }

      const previousAttempt = scheduled.get(txHash)?.attempt ?? 0;
      const attempt = previousAttempt + 1;
      // Re-read status: markSyncSlow above may have just flipped it. Once
      // slow, stop backing off further — fixed low-frequency cadence forever
      // (or until match) instead of clamping at the 13s fast-poll ceiling.
      const nowSlow = usePendingTradesStore.getState().trades[txHash]?.status === 'syncSlow';
      const delay = nowSlow ? SYNC_SLOW_POLL_MS : nextReconcileDelay(attempt);
      const timer = setTimeout(() => void tick(txHash), delay);
      scheduled.set(txHash, { timer, attempt });
    };

    // Schedule any newly-seen trade still awaiting reconciliation (pending or
    // syncSlow — syncSlow rows keep polling too, see tick()).
    for (const [txHash, trade] of Object.entries(trades)) {
      if (scheduled.has(txHash)) continue;
      if (trade.status !== 'pending' && trade.status !== 'syncSlow') continue;
      const timer = setTimeout(() => void tick(txHash), nextReconcileDelay(0));
      scheduled.set(txHash, { timer, attempt: 0 });
    }

    // Stop polling anything that vanished from the store (removed/reconciled
    // through another path, e.g. clearAllPendingTrades in tests).
    for (const txHash of Array.from(scheduled.keys())) {
      if (!trades[txHash]) {
        clearTimeout(scheduled.get(txHash)!.timer);
        scheduled.delete(txHash);
      }
    }
    // Intentionally keyed on the sorted txHash set, not `trades` itself — each
    // tick() call re-reads the store directly and re-schedules itself, so an
    // in-place status flip (pending->syncSlow) doesn't need this effect to
    // re-run.
  }, [Object.keys(trades).sort().join(',')]);

  useEffect(
    () => () => {
      for (const entry of scheduledRef.current.values()) clearTimeout(entry.timer);
      scheduledRef.current.clear();
    },
    []
  );
};

export const PendingTradesReconciler = () => {
  usePendingTradeReconciler();
  return null;
};
